"use client"

import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import NotificationTestButton from "../../components/NotificationTestButton"
import { Mail, MessageSquare, Clock, AlertCircle, CheckCircle2, XCircle, RefreshCw } from "lucide-react"
import { notificationTemplatesAPI, notificationSettingsAPI, followupScheduleAPI, smsProviderAPI } from "../../services/api"
import { useAuth } from "../../context/AuthContext"
import SettingsRailLayout from "../../components/settings-rail-layout"
import SfSwitch from "../../components/ui/sf-switch"

// Shared with the Appointment reminder page — same enum lives in
// lib/automated-sms-gate.js on the backend.
const PROVIDER_LABELS = {
  twilio: "Twilio",
  openphone: "OpenPhone",
  callio: "Callio",
  ringcentral: "RingCentral",
  leadbridge: "LeadBridge",
}

// Delay choices for post-job follow-ups. Hours AFTER completion.
// goscha's #76 clarification mentions "2 weeks after the last cleaning"
// as the recurring/bulk use case, so {336} is the natural default; the
// shorter options cover one-time thank-you flows.
const DELAY_CHOICES = [
  { hours: 24, label: "1 day after" },
  { hours: 72, label: "3 days after" },
  { hours: 168, label: "1 week after" },
  { hours: 336, label: "2 weeks after" },
  { hours: 720, label: "1 month after" },
  { hours: 2160, label: "3 months after" },
]

const JobFollowUp = () => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })
  const [enableEmail, setEnableEmail] = useState(true)
  const [enableSMS, setEnableSMS] = useState(true)
  const [activeTab, setActiveTab] = useState("sms")
  const [emailTemplate, setEmailTemplate] = useState({ subject: '', content: '' })
  const [smsTemplate, setSmsTemplate] = useState({ content: '' })
  const [showPreview, setShowPreview] = useState(false)

  // Follow-up schedule (migration 087).
  const [cronEnabled, setCronEnabled] = useState(false)
  const [delayHours, setDelayHours] = useState([336])
  const [scheduleLoading, setScheduleLoading] = useState(true)
  const [scheduleSaving, setScheduleSaving] = useState(false)
  const [scheduleError, setScheduleError] = useState('')

  // Recent follow-up dispatches.
  const [dispatches, setDispatches] = useState([])
  const [dispatchesLoading, setDispatchesLoading] = useState(true)
  const [dispatchesError, setDispatchesError] = useState('')

  // Shared notification channel — same backend endpoint as the
  // Appointment reminder page.
  const [smsProviderData, setSmsProviderData] = useState(null)
  const [smsProviderSaving, setSmsProviderSaving] = useState(false)
  const [smsProviderError, setSmsProviderError] = useState('')

  const navigate = useNavigate()
  const { user } = useAuth()

  const loadTemplatesAndSettings = useCallback(async () => {
    try {
      setLoading(true)
      const emailTemplates = await notificationTemplatesAPI.getTemplates(user.id, 'email', 'job_followup')
      if (emailTemplates.length > 0) {
        const t = emailTemplates[0]
        setEmailTemplate({ subject: t.subject || '', content: t.content || '' })
        setEnableEmail(t.is_enabled === 1)
      }
      const smsTemplates = await notificationTemplatesAPI.getTemplates(user.id, 'sms', 'job_followup')
      if (smsTemplates.length > 0) {
        const t = smsTemplates[0]
        setSmsTemplate({ content: t.content || '' })
        setEnableSMS(t.is_enabled === 1)
      }
      try {
        const settings = await notificationSettingsAPI.getSettings(user.id)
        if (Array.isArray(settings)) {
          const row = settings.find((s) => s.notification_type === 'job_followup')
          if (row) {
            setEnableEmail(row.email_enabled === 1)
            setEnableSMS(row.sms_enabled === 1)
          }
        }
      } catch (_) { /* settings optional */ }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load notification templates' })
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  const loadSchedule = useCallback(async () => {
    try {
      setScheduleLoading(true)
      setScheduleError('')
      const data = await followupScheduleAPI.getSchedule()
      setCronEnabled(Boolean(data?.enabled))
      setDelayHours(
        Array.isArray(data?.delay_hours) && data.delay_hours.length > 0
          ? data.delay_hours.slice().sort((a, b) => a - b)
          : [336]
      )
    } catch (e) {
      const code = e?.response?.data?.error
      setScheduleError(
        code === 'migration_missing'
          ? 'Database migration 087 not applied yet. Follow-up schedule unavailable until then.'
          : 'Could not load follow-up schedule.'
      )
    } finally {
      setScheduleLoading(false)
    }
  }, [])

  const loadDispatches = useCallback(async () => {
    try {
      setDispatchesLoading(true)
      setDispatchesError('')
      const rows = await followupScheduleAPI.getDispatches(20)
      setDispatches(rows)
    } catch (e) {
      const code = e?.response?.data?.error
      if (code !== 'migration_missing') {
        setDispatchesError('Could not load recent follow-up activity.')
      }
    } finally {
      setDispatchesLoading(false)
    }
  }, [])

  const loadSmsProvider = useCallback(async () => {
    try {
      setSmsProviderError('')
      const data = await smsProviderAPI.getProvider()
      setSmsProviderData(data)
    } catch (e) {
      const code = e?.response?.data?.error
      setSmsProviderError(
        code === 'migration_missing'
          ? 'Run migration 086 to enable the notification channel selector.'
          : 'Could not load notification channel.'
      )
    }
  }, [])

  useEffect(() => {
    if (!user?.id) return
    loadTemplatesAndSettings()
    loadSchedule()
    loadDispatches()
    loadSmsProvider()
  }, [user?.id, loadTemplatesAndSettings, loadSchedule, loadDispatches, loadSmsProvider])

  const toggleDelay = (hours) => {
    setDelayHours((prev) => {
      const has = prev.includes(hours)
      const next = has ? prev.filter((h) => h !== hours) : [...prev, hours]
      return next.sort((a, b) => a - b)
    })
  }

  const handleSaveSchedule = async () => {
    if (delayHours.length === 0) {
      setScheduleError('Pick at least one follow-up time, or turn follow-ups off.')
      return
    }
    try {
      setScheduleSaving(true)
      setScheduleError('')
      await followupScheduleAPI.updateSchedule({
        enabled: cronEnabled,
        delay_hours: delayHours,
      })
      setMessage({ type: 'success', text: 'Follow-up schedule saved.' })
      setTimeout(() => setMessage({ type: '', text: '' }), 2500)
    } catch (e) {
      const code = e?.response?.data?.error
      setScheduleError(
        code === 'migration_missing'
          ? 'Database migration 087 not applied yet.'
          : (e?.response?.data?.message || e.message || 'Could not save follow-up schedule.')
      )
    } finally {
      setScheduleSaving(false)
    }
  }

  const handleChangeProvider = async (nextValue) => {
    const provider = nextValue === '' ? null : nextValue
    setSmsProviderSaving(true)
    setSmsProviderError('')
    try {
      await smsProviderAPI.updateProvider(provider)
      await loadSmsProvider()
      setMessage({ type: 'success', text: 'Notification channel updated.' })
      setTimeout(() => setMessage({ type: '', text: '' }), 2500)
    } catch (e) {
      const code = e?.response?.data?.error
      setSmsProviderError(
        code === 'migration_missing'
          ? 'Run migration 086 to enable the notification channel selector.'
          : (e?.response?.data?.message || e.message || 'Could not save notification channel.')
      )
    } finally {
      setSmsProviderSaving(false)
    }
  }

  const handleSaveTemplates = async () => {
    try {
      setSaving(true)
      await notificationTemplatesAPI.updateTemplate({
        userId: user.id,
        templateType: 'email',
        notificationName: 'job_followup',
        subject: emailTemplate.subject,
        content: emailTemplate.content,
        isEnabled: enableEmail,
      })
      await notificationTemplatesAPI.updateTemplate({
        userId: user.id,
        templateType: 'sms',
        notificationName: 'job_followup',
        subject: null,
        content: smsTemplate.content,
        isEnabled: enableSMS,
      })
      await notificationSettingsAPI.updateSetting({
        userId: user.id,
        notificationType: 'job_followup',
        emailEnabled: enableEmail,
        smsEnabled: enableSMS,
        pushEnabled: false,
      })
      setMessage({ type: 'success', text: 'Follow-up templates saved.' })
      setTimeout(() => setMessage({ type: '', text: '' }), 2500)
    } catch (e) {
      setMessage({ type: 'error', text: 'Failed to save follow-up templates' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <SettingsRailLayout title="Job follow-up" section="Communications" subtitle="After-job thank-you, review request, or promotion">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="space-y-6">

          {/* Email / SMS tabs + enable toggles */}
          <div className="bg-white rounded-lg border border-[var(--sf-border-light)] p-6">
            <div className="flex space-x-1 bg-[var(--sf-bg-page)] rounded-lg p-1 mb-6">
              <button
                onClick={() => setActiveTab("email")}
                className={`flex-1 flex items-center justify-center space-x-2 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  activeTab === "email" ? "bg-white text-[var(--sf-blue-500)] shadow-sm" : "text-[var(--sf-text-secondary)] hover:text-[var(--sf-text-primary)]"
                }`}
              >
                <Mail className="w-4 h-4" />
                <span>Email</span>
              </button>
              <button
                onClick={() => setActiveTab("sms")}
                className={`flex-1 flex items-center justify-center space-x-2 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  activeTab === "sms" ? "bg-white text-green-600 shadow-sm" : "text-[var(--sf-text-secondary)] hover:text-[var(--sf-text-primary)]"
                }`}
              >
                <MessageSquare className="w-4 h-4" />
                <span>SMS</span>
              </button>
            </div>

            {activeTab === "email" ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-[var(--sf-text-primary)]">Enable Follow-up Email</h3>
                    <p className="text-sm text-[var(--sf-text-secondary)] mt-1">
                      Sent automatically after a job is completed.
                    </p>
                  </div>
                  <SfSwitch
                    checked={enableEmail}
                    onChange={setEnableEmail}
                    showLabel
                    ariaLabel="Enable Follow-up Email"
                  />
                </div>
                <NotificationTestButton
                  notificationType="Job Follow-up"
                  messageType="email"
                  templateContent={emailTemplate.content || "<p>Thank you for choosing us!</p>"}
                />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-[var(--sf-text-primary)]">Enable Follow-up SMS</h3>
                    <p className="text-sm text-[var(--sf-text-secondary)] mt-1">
                      Sent automatically after a job is completed.
                    </p>
                  </div>
                  <SfSwitch
                    checked={enableSMS}
                    onChange={setEnableSMS}
                    showLabel
                    ariaLabel="Enable Follow-up SMS"
                  />
                </div>
                <NotificationTestButton
                  notificationType="Job Follow-up"
                  messageType="sms"
                  templateContent={smsTemplate.content || "Thanks for choosing us! We'd love your feedback."}
                />
              </div>
            )}
          </div>

          {/* Notification channel — shared with the Reminder page */}
          <div className="bg-white rounded-lg border border-[var(--sf-border-light)] p-6">
            <div className="space-y-3">
              <div>
                <h3 className="font-medium text-[var(--sf-text-primary)] flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-[var(--sf-blue-500)]" />
                  Notification channel
                </h3>
                <p className="text-sm text-[var(--sf-text-secondary)] mt-1">
                  Shared with Reminders. Picks the first connected sendable channel automatically if left on <em>Auto</em>.
                </p>
              </div>

              {smsProviderError && (
                <div className="text-xs text-red-600 flex items-start gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>{smsProviderError}</span>
                </div>
              )}

              {smsProviderData ? (
                <>
                  <select
                    value={smsProviderData.selected || ''}
                    onChange={(e) => handleChangeProvider(e.target.value)}
                    disabled={smsProviderSaving}
                    className="w-full px-3 py-2 text-sm border border-[var(--sf-border-light)] rounded-md bg-white"
                  >
                    <option value="">Auto (pick first connected)</option>
                    {smsProviderData.providers.map((p) => {
                      const label = PROVIDER_LABELS[p.id] || p.id
                      const suffix = !p.connected
                        ? ' — not connected'
                        : !p.send_implemented
                          ? ' — send coming soon'
                          : ''
                      return <option key={p.id} value={p.id}>{label}{suffix}</option>
                    })}
                  </select>
                  <div className="text-xs text-[var(--sf-text-muted)]">
                    {smsProviderData.effective?.ok ? (
                      <span className="text-green-700">
                        Active: sends via <strong>{PROVIDER_LABELS[smsProviderData.effective.provider] || smsProviderData.effective.provider}</strong>
                        {smsProviderData.effective.autoPicked ? ' (auto-picked)' : ''}.
                      </span>
                    ) : smsProviderData.effective?.reason === 'no_provider_connected' ? (
                      <span className="text-amber-700">
                        No SMS provider is connected yet. Visit the Communication hub to connect one.
                      </span>
                    ) : smsProviderData.effective?.reason === 'provider_send_not_implemented_yet' ? (
                      <span className="text-amber-700">
                        <strong>{PROVIDER_LABELS[smsProviderData.effective.provider]}</strong> is connected, but sending through it isn't wired up yet. Pick <strong>Twilio</strong> (if connected) in the meantime.
                      </span>
                    ) : smsProviderData.effective?.reason === 'provider_not_connected' ? (
                      <span className="text-amber-700">
                        <strong>{PROVIDER_LABELS[smsProviderData.effective.provider]}</strong> isn't connected on this tenant.
                      </span>
                    ) : null}
                  </div>
                </>
              ) : (
                <div className="text-sm text-[var(--sf-text-muted)]">Loading…</div>
              )}
            </div>
          </div>

          {/* Follow-up schedule */}
          <div className="bg-white rounded-lg border border-[var(--sf-border-light)] p-6">
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-medium text-[var(--sf-text-primary)] flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[var(--sf-blue-500)]" />
                    Follow-up schedule
                  </h3>
                  <p className="text-sm text-[var(--sf-text-secondary)] mt-1">
                    How long after a job completes to send the follow-up. Multiple times OK — one SMS per delay, per job.
                  </p>
                </div>
                <div className="shrink-0">
                  <SfSwitch
                    checked={cronEnabled}
                    onChange={setCronEnabled}
                    disabled={scheduleLoading}
                    showLabel
                    ariaLabel="Automated follow-ups on/off"
                  />
                </div>
              </div>

              {scheduleLoading ? (
                <div className="text-sm text-[var(--sf-text-muted)]">Loading schedule…</div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {DELAY_CHOICES.map((c) => {
                    const checked = delayHours.includes(c.hours)
                    return (
                      <label
                        key={c.hours}
                        className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm cursor-pointer transition ${
                          checked
                            ? 'bg-[var(--sf-blue-50,#eff6ff)] border-[var(--sf-blue-500)] text-[var(--sf-text-primary)]'
                            : 'bg-white border-[var(--sf-border-light)] text-[var(--sf-text-secondary)] hover:border-[var(--sf-text-muted)]'
                        } ${!cronEnabled ? 'opacity-60' : ''}`}
                      >
                        <input
                          type="checkbox"
                          className="rounded border-[var(--sf-border-light)]"
                          checked={checked}
                          onChange={() => toggleDelay(c.hours)}
                          disabled={!cronEnabled}
                        />
                        {c.label}
                      </label>
                    )
                  })}
                </div>
              )}

              {scheduleError && (
                <div className="text-xs text-red-600 flex items-start gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>{scheduleError}</span>
                </div>
              )}

              <div className="flex items-center justify-between pt-2 border-t border-[var(--sf-border-light)]">
                <p className="text-xs text-[var(--sf-text-muted)]">
                  {cronEnabled
                    ? `Follow-ups will fire ${delayHours.length} time${delayHours.length === 1 ? '' : 's'} per job.`
                    : 'Automated follow-ups are off. Turn on to activate the schedule.'}
                </p>
                <button
                  onClick={handleSaveSchedule}
                  disabled={scheduleSaving || scheduleLoading}
                  className="px-3 py-1.5 text-sm font-medium rounded-md bg-[var(--sf-blue-500)] text-white hover:opacity-90 disabled:opacity-50"
                >
                  {scheduleSaving ? 'Saving…' : 'Save schedule'}
                </button>
              </div>
            </div>
          </div>

          {/* Recent follow-up activity */}
          <div className="bg-white rounded-lg border border-[var(--sf-border-light)] p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-[var(--sf-text-primary)]">Recent follow-up activity</h3>
              <button
                type="button"
                onClick={loadDispatches}
                className="text-xs text-[var(--sf-blue-500)] hover:underline flex items-center gap-1"
                disabled={dispatchesLoading}
              >
                <RefreshCw className={`w-3 h-3 ${dispatchesLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
            {dispatchesLoading ? (
              <div className="text-sm text-[var(--sf-text-muted)]">Loading…</div>
            ) : dispatchesError ? (
              <div className="text-xs text-red-600">{dispatchesError}</div>
            ) : dispatches.length === 0 ? (
              <div className="text-sm text-[var(--sf-text-muted)]">
                No follow-ups dispatched yet. Each dispatch (sent, skipped, or failed) will show here once completed jobs cross one of your delays above.
              </div>
            ) : (
              <div className="divide-y divide-[var(--sf-border-light)] -my-2">
                {dispatches.map((d) => {
                  const when = d.dispatchedAt ? new Date(d.dispatchedAt).toLocaleString() : ''
                  const StatusIcon = d.status === 'sent' ? CheckCircle2 : d.status === 'failed' ? XCircle : AlertCircle
                  const statusTone = d.status === 'sent' ? 'text-green-600' : d.status === 'failed' ? 'text-red-600' : 'text-amber-600'
                  return (
                    <div key={d.id} className="py-3 flex items-start gap-3">
                      <StatusIcon className={`w-4 h-4 mt-0.5 shrink-0 ${statusTone}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-[var(--sf-text-primary)]">
                          <span className="font-medium">{d.serviceName || `Job #${d.jobId}`}</span>
                          {d.customerName ? <span className="text-[var(--sf-text-muted)]"> · {d.customerName}</span> : null}
                        </div>
                        <div className="text-xs text-[var(--sf-text-muted)] mt-0.5">
                          {d.delayHours}h after · {d.channel.toUpperCase()} · {when}
                        </div>
                        {d.status === 'skipped' && d.skipReason && (
                          <div className="text-xs text-amber-700 mt-0.5">skipped — {d.skipReason.replace(/_/g, ' ')}</div>
                        )}
                        {d.status === 'failed' && d.errorMessage && (
                          <div className="text-xs text-red-700 mt-0.5">failed — {d.errorMessage}</div>
                        )}
                        {d.status === 'sent' && d.providerMessageId && (
                          <div className="text-xs text-green-700 mt-0.5 font-mono">sent · {d.providerMessageId}</div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Save templates toast */}
          {message.text && (
            <div className={`text-sm px-3 py-2 rounded-md ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {message.text}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              onClick={handleSaveTemplates}
              disabled={saving || loading}
              className="px-4 py-2 text-sm font-semibold text-white bg-[var(--sf-blue-500)] rounded-md hover:bg-[var(--sf-blue-600)] disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save templates'}
            </button>
          </div>
        </div>
      </div>
    </SettingsRailLayout>
  )
}

export default JobFollowUp
